import { currentAuthToken } from "@homebound/auth-components";
import { useToast } from "@homebound/beam";
import { useCallback } from "react";
import { createExportCsvUrl } from "src/RouteUrls";

/**
 * Downloads a CSV file from the server via POST to accommodate potentially large filters
 */
export function useCsvExportPost<T extends Record<string, unknown>>(type: string, parameters: T) {
  const { showToast } = useToast();
  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(createExportCsvUrl(type), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await currentAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(parameters),
      });

      if (!response.ok) {
        showToast({ message: "Download failed with status " + response.status, type: "error" });
        return;
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = url;
      link.download = response.headers.get("Content-Disposition")?.split("filename=")[1] ?? "takeoff.csv";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      showToast({ message: `Download failed: ${error.message}`, type: "error" });
    }
  }, [type, parameters, showToast]);

  return { handleDownload };
}
